import React, { useState, useEffect, useRef } from "react";
import {
  Avatar,
  Box,
  Divider,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  TextField,
  Typography,
  useMediaQuery,
  InputAdornment,
} from "@material-ui/core";
import { useNotify } from "react-admin";
import CloseOutlinedIcon from "@material-ui/icons/CloseOutlined";
import { performUpdateInventoryMini } from "../../../graphql/inventory/inventoryCRUD";
import { InspectionCounter } from "../../../components/common/InspectionCounter";
import { logError } from "../../../utils/appUtils";

import DoubleCheckIcon from "@material-ui/icons/DoneAll";

import DoneIcon from "@material-ui/icons/Done";
import KeyboardArrowRightIcon from "@material-ui/icons/KeyboardArrowRight";
import PendingApprovalIcon from "@material-ui/icons/PriorityHigh";
import ReviewOutlinedIcon from "@material-ui/icons/FindInPageOutlined";
import XIcon from "@material-ui/icons/CloseOutlined";

import {
  AreaType,
  InventoryStatus,
  WorkOrderState,
  UserType,
} from "../../../utils/constants";

import { getUnitName } from "../../../utils/loadTypeUtils";
import { getInventoryPrimaryField } from "../../../utils/utils";
import { ShipmentListItemMenu } from "./ShipmentListItemMenu";

const getbyProductNameKey = (inventory) =>
  `${inventory.areaId}-${inventory.zoneId}-${inventory.productName}-${inventory.inventoryUOM}`;
const getbyProductIdKey = (inventory) =>
  `${inventory.areaId}-${inventory.zoneId}-${inventory.productId}-${inventory.inventoryUOM}`;
const QUANTITY_PLACEHOLDER = "QUANTITY_PLACEHOLDER";

export const InboundInventoryList = ({
  classes,
  currentShipment,
  cloneableInventoriesByProductName,
  cloneableInventoriesByProductId,
  handleClone,
  match,
  history,
  disabled,
}) => {
  const [searchInventories, setSearchInventories] = useState([]);
  const [term, setTerm] = useState("");
  const xsDown = useMediaQuery((theme) => theme.breakpoints.down("xs"));
  const precountSaveTimersRef = useRef({});
  const notify = useNotify();

  useEffect(() => {
    setSearchInventories([...currentShipment.inventories.items]);
  }, [
    currentShipment,
    cloneableInventoriesByProductName,
    cloneableInventoriesByProductId,
  ]);

  useEffect(
    () => () => {
      const timers = precountSaveTimersRef.current;
      Object.keys(timers).forEach((key) => {
        clearTimeout(timers[key]);
      });
    },
    [],
  );

  const getIcon = (inventory) => {
    if (inventory.verificationRequired === "true")
      return <PendingApprovalIcon fontSize="inherit" />;
    else if (inventory.status === InventoryStatus.UNLOADED)
      return <DoneIcon fontSize="inherit" />;
    else if (inventory.status === InventoryStatus.REVIEW)
      return <ReviewOutlinedIcon fontSize="inherit" />;
    else if (inventory.status === InventoryStatus.STORED)
      return <DoubleCheckIcon fontSize="inherit" />;
    return <CloseOutlinedIcon fontSize="inherit" />;
  };

  const getAvatarClassName = (inventory) => {
    const precountMismatch = hasPrecountMismatch(inventory);
    if (precountMismatch) {
      return classes.red;
    } else if (
      inventory.verificationRequired === "true" ||
      inventory.verificationRequired === "resolved"
    )
      return classes.amber;
    else if (
      inventory.status === InventoryStatus.UNLOADED ||
      inventory.status === InventoryStatus.STORED
    )
      return classes.green;

    return classes.unprocessedItem;
  };

  const handleSelect = (inventory) => {
    history.push({ pathname: match.url + "/inventories/" + inventory.id });
  };

  const getListItem = (inventory) => {
    const primaryField = getInventoryPrimaryField(
      currentShipment.account,
      inventory,
    );

    const qtyText = !inventory.quantityReceived
      ? `${inventory.quantityAdvised} ${getUnitName(inventory.inventoryUOM)}`
      : `${inventory.quantityReceived} / ${
          inventory.quantityAdvised
        } ${getUnitName(inventory.inventoryUOM)}`;

    const productLabel =
      inventory.productName || inventory.productCode || "Unnamed product";
    let primaryText = productLabel;
    if (!xsDown && window.appUser.type !== UserType.CREW)
      primaryText = `${primaryText} - ${qtyText}`;

    let secondaryText = `${primaryField.label} : ${primaryField.value}`;
    if (xsDown && window.appUser.type !== UserType.CREW)
      secondaryText = (
        <>
          {qtyText}
          <br />
          {primaryField.label} : {primaryField.value}
        </>
      );

    return <ListItemText primary={primaryText} secondary={secondaryText} />;
  };

  const clear = () => {
    setTerm("");
    setSearchInventories(currentShipment.inventories.items);
  };

  const handleSearch = (evt) => {
    const value = evt.target.value;
    setTerm(value);
    if (!value) {
      setSearchInventories(currentShipment.inventories.items);
    } else {
      const searchTerm = value.toLowerCase();
      const searchResult = currentShipment.inventories.items.filter((itm) => {
        if (
          itm.customFields?.toString().toLowerCase().includes(searchTerm) ||
          itm.productName.toLowerCase().includes(searchTerm)
        ) {
          return true;
        }
        return false;
      });
      setSearchInventories(searchResult);
    }
  };

  const handleSubmit = (evt) => {
    if (evt.key === "Enter") {
      handleSearch(evt);
    }
  };

  const getPossiblePlacementLocations = (inventory) => {
    const byProductNameKey = getbyProductNameKey(inventory);
    const byProductIdKey = getbyProductIdKey(inventory);
    let possibleLocations;
    if (cloneableInventoriesByProductName[byProductNameKey])
      possibleLocations = Object.values(
        cloneableInventoriesByProductName[byProductNameKey],
      );
    else if (cloneableInventoriesByProductId[byProductIdKey])
      possibleLocations = Object.values(
        cloneableInventoriesByProductId[byProductIdKey],
      );
    if (possibleLocations) {
      possibleLocations.forEach(
        (itm) =>
          (itm.name = itm.name.replace(
            QUANTITY_PLACEHOLDER,
            inventory.quantityAdvised,
          )),
      );
      possibleLocations.sort((a, b) => a.name.localeCompare(b.name));
      return possibleLocations;
    }
    return [];
  };

  // placeabe = true if same product && not TRUCK && not BULK && UNMANAGED
  const isPlaceable = (inventory) => {
    if (
      inventory.status === InventoryStatus.ARRIVED &&
      currentShipment.area.areaType !== AreaType.BULK &&
      currentShipment.area.areaType !== AreaType.TRACK
    ) {
      const byProductNameKey = getbyProductNameKey(inventory);
      if (cloneableInventoriesByProductName[byProductNameKey]) return true;
      const byProductIdKey = getbyProductIdKey(inventory);
      if (cloneableInventoriesByProductId[byProductIdKey]) return true;
    }
    return false;
  };

  const getListItemAction = (inventory) => {
    if (isPlaceable(inventory)) {
      return (
        <ShipmentListItemMenu
          getPossiblePlacementLocations={getPossiblePlacementLocations}
          handleOpen={handleSelect}
          handleClone={handleClone}
          inventory={inventory}
        />
      );
    }
    return (
      <IconButton edge="end" onClick={() => handleSelect(inventory)}>
        <KeyboardArrowRightIcon fontSize="large" />
      </IconButton>
    );
  };

  if (!currentShipment.inventories.items) return <></>;

  const shouldshowCounter =
    currentShipment?.reloadYardCommodity?.commodity?.enableCounter &&
    currentShipment?.reloadYardCommodity?.showCounter &&
    currentShipment?.state === WorkOrderState.READY;

  const normalizeNumber = (value) => {
    if (value === null || value === undefined || value === "") return null;
    const num = Number(value);
    return Number.isNaN(num) ? null : num;
  };

  const hasPrecountMismatch = (inventory) => {
    if (!shouldshowCounter) return false;

    const precount = normalizeNumber(inventory.quantityPrecount);
    const advised = normalizeNumber(inventory.quantityAdvised);

    if (precount === null || advised === null) return false;

    return precount !== advised;
  };

  const updateInventoryPrecount = (inventoryId, nextValue) => {
    // Update local tally sheet list (UI state)
    setSearchInventories((prev) =>
      prev.map((inv) =>
        inv.id === inventoryId ? { ...inv, quantityPrecount: nextValue } : inv,
      ),
    );

    // Also update the current work order inventories so WorkOrderDetailsCard
    // includes quantityPrecount in the next save payload.
    if (currentShipment?.inventories?.items) {
      currentShipment.inventories.items = currentShipment.inventories.items.map(
        (inv) =>
          inv.id === inventoryId
            ? { ...inv, quantityPrecount: nextValue }
            : inv,
      );
    }

    // Debounce persistence to avoid too many requests on rapid +/- clicks
    const timers = precountSaveTimersRef.current;
    if (timers[inventoryId]) {
      clearTimeout(timers[inventoryId]);
    }

    timers[inventoryId] = setTimeout(() => {
      performUpdateInventoryMini({
        id: inventoryId,
        quantityPrecount: nextValue,
      }).catch((err) => {
        // Debounced save: still surface failures so users notice their
        // tally-sheet edits did not persist (main Accept/Complete flow
        // re-saves later, but stale precounts are confusing).
        logError(
          "workOrder:InboundInventoryList:savePrecount",
          err,
          notify,
        );
      });
      delete timers[inventoryId];
    }, 500);
  };

  const handlePrecountDelta = (inventory, delta) => {
    const current = inventory.quantityPrecount ?? 0;
    const next = Math.max(0, current + delta);
    updateInventoryPrecount(inventory.id, next);
  };

  const handlePrecountInputChange = (inventory, rawValue) => {
    const trimmed = String(rawValue ?? "").trim();
    if (trimmed === "") {
      updateInventoryPrecount(inventory.id, null);
      return;
    }
    const parsed = Number(trimmed);
    if (Number.isNaN(parsed)) return;
    updateInventoryPrecount(inventory.id, Math.max(0, parsed));
  };

  return (
    <>
      <Grid container item xs={12}>
        {xsDown ? (
          <>
            <Grid item xs={12}>
              <Typography variant="h6" color="textSecondary" gutterBottom>
                Tally Sheet
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <TextField
                id="search"
                name="search"
                size="small"
                fullWidth={true}
                variant="outlined"
                onChange={handleSearch}
                value={term}
                placeholder="Search"
                onKeyDown={handleSubmit}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton disableRipple onClick={clear}>
                        <XIcon className={classes.clearButton} />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
          </>
        ) : (
          <Grid item xs={12}>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
            >
              <Typography variant="h6" color="textSecondary">
                Tally Sheet
              </Typography>
              <TextField
                id="search"
                name="search"
                size="small"
                variant="outlined"
                onChange={handleSearch}
                value={term}
                placeholder="Search"
                onKeyDown={handleSubmit}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton disableRipple onClick={clear}>
                        <XIcon className={classes.clearButton} />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
          </Grid>
        )}
      </Grid>
      <Grid item xs={12}>
        <List>
          {searchInventories.map((inventory, idx) => {
            return (
              <div key={inventory.id}>
                {disabled ? (
                  <ListItem style={{ paddingLeft: "0px", paddingRight: "0px" }}>
                    <ListItemAvatar>
                      <Avatar className={getAvatarClassName(inventory)}>
                        {hasPrecountMismatch(inventory)
                          ? "≠"
                          : getIcon(inventory)}
                      </Avatar>
                    </ListItemAvatar>
                    <Box
                      display="flex"
                      alignItems="center"
                      justifyContent="space-between"
                      width="100%"
                    >
                      {getListItem(inventory)}
                      <Box display="flex" alignItems="center">
                        {shouldshowCounter && (
                          <InspectionCounter
                            value={inventory.quantityPrecount}
                            isSmall={xsDown}
                            onDelta={(delta) =>
                              handlePrecountDelta(inventory, delta)
                            }
                            onInputChange={(raw) =>
                              handlePrecountInputChange(inventory, raw)
                            }
                          />
                        )}
                        {/* No getListItemAction here since row is disabled */}
                      </Box>
                    </Box>
                  </ListItem>
                ) : (
                  <ListItem onClick={() => handleSelect(inventory)}>
                    <ListItemAvatar>
                      <Avatar className={getAvatarClassName(inventory)}>
                        {hasPrecountMismatch(inventory)
                          ? "≠"
                          : getIcon(inventory)}
                      </Avatar>
                    </ListItemAvatar>
                    <Box
                      display="flex"
                      alignItems="center"
                      justifyContent="space-between"
                      width="100%"
                    >
                      {getListItem(inventory)}
                      <Box display="flex" alignItems="center">
                        {shouldshowCounter && (
                          <InspectionCounter
                            value={inventory.quantityPrecount}
                            isSmall={xsDown}
                            onDelta={(delta) =>
                              handlePrecountDelta(inventory, delta)
                            }
                            onInputChange={(raw) =>
                              handlePrecountInputChange(inventory, raw)
                            }
                          />
                        )}
                        {getListItemAction(inventory)}
                      </Box>
                    </Box>
                  </ListItem>
                )}
                {idx < searchInventories.length - 1 ? <Divider /> : null}
              </div>
            );
          })}
        </List>
        <Divider />
      </Grid>
    </>
  );
};
